import Bugsnag from "@bugsnag/js";
import {
  QueryClient,
  useQuery,
  useMutation,
  useQueryClient,
  UseMutationOptions,
} from "@tanstack/react-query";
import axios, { AxiosError, AxiosResponse } from "axios";
import _isEmpty from "lodash/isEmpty";
import { useRouter } from "next/router";
import { env } from "next-runtime-env";
import { ParsedQs } from "qs";

import {
  CanvasDocumentData,
  ProductDocumentData,
  FormatsDocumentData,
  ExtrasDocumentData,
} from "prismicio-types";
import { SSR_CONFIGURATION_ERROR } from "utils/error-utils";
import { useGetCombinedCustomisation } from "utils/schema-utils";
import { getNormalisedParsedQuery, getNormalisedUrl } from "utils/url-utils";

import {
  SublimatorProductsResponse,
  TocError,
  TocResponse,
  SchemaResponse,
  UseGetTocResponse,
  RedirectUrlResponse,
  UseGetRedirectUrlResponse,
  SublimatorContentResponse,
  SublimatorFormatsResponse,
  UseGetSchemaResponse,
  FlagsResponse,
  UseGetFlagsResponse,
  UseGetCustomisationResponse,
  EagleVariants,
  SublimatorVariantsResponse,
  SetCustomisationArgs,
  SublimatorExtrasResponse,
} from "../types/queries";

/**
 * Product query keys.
 *
 * These uniquely identify queries in the `react-query` cache.
 *
 * [More on this pattern](https://tkdodo.eu/blog/effective-react-query-keys)
 */
export const productKeys = {
  flags: [
    {
      scope: "product",
      data: "flags",
    },
  ] as const,
  toc: (museSlug: string, customisation: ParsedQs) =>
    [
      {
        scope: "product",
        museSlug,
        customisation,
        data: "toc",
      },
    ] as const,
  schema: (museSlug: string, customisation: ParsedQs) =>
    [
      {
        scope: "product",
        museSlug,
        customisation,
        data: "schema",
      },
    ] as const,
  product: (solidusSlug: string, marketplace: string) =>
    [
      {
        scope: "product",
        solidusSlug,
        marketplace,
        data: "product",
      },
    ] as const,
  redirectUrl: (productSku: string, customisation: ParsedQs) =>
    [
      {
        scope: "product",
        productSku,
        customisation,
        data: "redirectUrl",
      },
    ] as const,
  customisation: [
    {
      scope: "product",
      data: "customisation",
    },
  ] as const,
};

/**
 * Set defaults for product queries
 *
 * Setting these here applies them globally whether called directly or via custom hooks
 *
 * Also helpful for testing as they can be easily overriden
 * (e.g. to stop retries from slowing tests down)
 */
export function setProductQueryDefaults(queryClient: QueryClient) {
  /**
   * Don't consider any product data as stale
   */
  queryClient.setQueryDefaults([{ data: "flags" }], {
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  queryClient.setQueryDefaults([{ data: "toc" }], {
    staleTime: Infinity,
    cacheTime: Infinity,
    // This stops the toc returning `undefined` between changes
    keepPreviousData: true,
    retry: (failureCount, error) => {
      /**
       * Don't retry if response is an array; assume these are field errors.
       *
       * Otherwise retries (which increase in delay) will slow the response.
       */
      if (Array.isArray(error.response?.data)) {
        return false;
      }
      // Otherwise retry up to three times
      return failureCount < 3;
    },
  });

  queryClient.setQueryDefaults([{ data: "schema" }], {
    staleTime: Infinity,
    cacheTime: Infinity,
    // This stops the schema returning `undefined` between changes
    keepPreviousData: true,
  });

  queryClient.setQueryDefaults([{ data: "product" }], {
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  queryClient.setQueryDefaults([{ data: "redirectUrl" }], {
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  queryClient.setQueryDefaults([{ data: "customisation" }], {
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: (failureCount) => failureCount < 1,
  });
}

/************************************************************************************************
 * QUERIES
 *
 * For each query, we define a hook and a fetcher function.
 *
 * Each hook wraps `useQuery` with configuration on when to fetch and parameters.
 *
 * The fetcher is separate from the hook for use outside of React components (e.g. on server).
 ************************************************************************************************/

/**
 * Fetch feature flags from local api
 */
export async function getFlags() {
  const response: AxiosResponse<FlagsResponse> =
    await axios.get("/_canvas/api/flags");

  return response.data;
}

/**
 * Return the active feature flags.
 */
export function useGetFlags() {
  return useQuery<UseGetFlagsResponse>(productKeys.flags, getFlags);
}

/**
 * Fetch toc (Table of Contents) and trigger TocUpdateEvent
 */
export async function getToc(
  ...[museSlug, customisation]: Parameters<(typeof productKeys)["toc"]>
) {
  const response: AxiosResponse<TocResponse> = await axios.post(
    env("NEXT_PUBLIC_MU2_URL") +
      "/product/" +
      (customisation.muse_slug_override ?? museSlug) +
      "/toc",
    {
      ...customisation,
      product_slug: museSlug,
      // ^ This ensures multi-variant books know which covers etc. to show}
    }
  );

  return response.data.toc;
}

/**
 * Return the toc for the current slug and all URL customisation.
 */
export function useGetToc() {
  const { data: product } = useGetProduct();
  const { data: customisation } = useGetCustomisation();

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }

  return useQuery<UseGetTocResponse, AxiosError<TocError>>(
    productKeys.toc(product.museSlug, customisation!),
    async () => getToc(product.museSlug, customisation!),
    {
      // Wait for localStorage to load
      enabled: !!customisation,
    }
  );
}

/**
 * Fetch product schema
 */
export async function getSchema(
  ...[museSlug, customisation]: Parameters<(typeof productKeys)["schema"]>
) {
  const response: AxiosResponse<SchemaResponse> = await axios.post(
    env("NEXT_PUBLIC_MU2_URL") +
      "/product/" +
      (customisation.muse_slug_override ?? museSlug) +
      "/schema/preview",
    {
      ...customisation,
      product_slug: museSlug,
      // ^ This ensures multi-variant books know which covers etc. to show
    }
  );

  const { height, width } = response.data.dimensions.page;

  return {
    aspect: width / height,
    keys: Object.values(response.data.schemas)[0].keys,
  };
}

/**
 * Return the schema for the current slug and all URL customisation.
 */
export function useGetSchema() {
  const { data: product } = useGetProduct();
  const { data: customisation } = useGetCustomisation();

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }

  return useQuery<UseGetSchemaResponse, AxiosError>(
    productKeys.schema(product.museSlug, customisation!),
    async () => getSchema(product.museSlug, customisation!),
    {
      enabled: !!customisation,
      onSuccess: (data) => {
        // Pop the aspect ratio into a CSS var for clean use throughout
        document.documentElement.style.setProperty(
          "--aspect",
          String(data.aspect)
        );
      },
    }
  );
}

/**
 * Fetch localised text content from Prismic CMS
 *
 * SERVER ONLY – the Prismic token env var won't be available on the client.
 */
export async function getProduct(
  solidusSlug: string,
  marketplace: string,
  prismicRef?: string
) {
  if (typeof window === "object") {
    throw new Error(
      "Sublimator content cannot be fetched from the client - request on the server instead"
    );
  }

  const [
    productResponse,
    eagleVariantsResponse,
    canvasContentResponse,
    formatsContentResponse,
    extrasContentResponse,
  ] = await Promise.allSettled([
    // Get Product data
    // Product Prismic content & Eagle data
    axios.get<SublimatorProductsResponse>(
      env("NEXT_PUBLIC_SUBLIMATOR_API_URL") + "/api/v1/products/" + solidusSlug,
      {
        headers: {
          Authorization: `Bearer ${env("SUBLIMATOR_API_KEY")}`,
          "X-Wonderbly-Marketplace-Id": marketplace,
        },
        params: {
          ...(prismicRef ? { canvas_prismic_ref: prismicRef } : {}),
        },
      }
    ),
    // Get Eagle variants
    // Used to display pricing for different covers on the formats page.
    // Currently only used on products with uses_pdc_formats_page enabled in their Prismic product doc.
    axios.get<SublimatorVariantsResponse>(
      env("NEXT_PUBLIC_SUBLIMATOR_API_URL") +
        "/api/v1/products/" +
        solidusSlug +
        "/variants",
      {
        headers: {
          Authorization: `Bearer ${env("SUBLIMATOR_API_KEY")}`,
          "X-Wonderbly-Marketplace-Id": marketplace,
        },
        params: {
          ...(prismicRef ? { canvas_prismic_ref: prismicRef } : {}),
        },
      }
    ),
    // Get generic Canvas content
    axios.get<SublimatorContentResponse>(
      env("NEXT_PUBLIC_SUBLIMATOR_API_URL") +
        "/api/v1/content/prismic/canvas/documents/canvas/canvas",
      {
        headers: {
          Authorization: `Bearer ${env("SUBLIMATOR_API_KEY")}`,
          "X-Wonderbly-Marketplace-Id": marketplace,
        },
        params: {
          ...(prismicRef ? { prismic_ref: prismicRef } : {}),
        },
      }
    ),
    // Get Formats content
    axios.get<SublimatorFormatsResponse>(
      env("NEXT_PUBLIC_SUBLIMATOR_API_URL") +
        "/api/v1/content/prismic/canvas/documents/formats/formats",
      {
        headers: {
          Authorization: `Bearer ${env("SUBLIMATOR_API_KEY")}`,
          "X-Wonderbly-Marketplace-Id": marketplace,
        },
        params: {
          ...(prismicRef ? { prismic_ref: prismicRef } : {}),
        },
      }
    ),
    // Get Extras content
    axios.get<SublimatorExtrasResponse>(
      env("NEXT_PUBLIC_SUBLIMATOR_API_URL") +
        "/api/v1/content/prismic/canvas/documents/extras/extras",
      {
        headers: {
          Authorization: `Bearer ${env("SUBLIMATOR_API_KEY")}`,
          "X-Wonderbly-Marketplace-Id": marketplace,
        },
        params: {
          ...(prismicRef ? { prismic_ref: prismicRef } : {}),
        },
      }
    ),
  ]);

  let canvas = {} as CanvasDocumentData;
  let formats = {} as FormatsDocumentData;
  let extras = {} as ExtrasDocumentData;
  let variants = [] as EagleVariants;

  // No bother if canvas content rejects, just fallback to English
  if (canvasContentResponse.status === "fulfilled") {
    canvas = canvasContentResponse.value.data.data.data;
  }

  if (eagleVariantsResponse.status === "fulfilled") {
    variants = eagleVariantsResponse.value.data.data;
  }

  if (formatsContentResponse.status === "fulfilled") {
    formats = formatsContentResponse.value.data.data.data;
  }

  if (extrasContentResponse.status === "fulfilled") {
    extras = extrasContentResponse.value.data.data.data;
  }

  // Rejection occurs when the Sublimator API fails or the slug returns a `404`
  if (productResponse.status === "rejected") {
    // On production, missing product documents will `500`
    if (env("NEXT_PUBLIC_CANVAS_ENV") === "production") {
      const error = Error(
        `Sublimator product request for market "${marketplace}" and slug "${solidusSlug}" failed with reason: "${productResponse.reason}"`
      );

      Bugsnag.notify(error, (event) => {
        event.severity = "error";
        event.unhandled = false;

        event.addMetadata("Metadata", {
          marketplace,
          solidusSlug,
          prismicRef,
          productResponse,
        });
      });

      throw error;
    }

    // Off production, stub fields to render the book
    return {
      usesPdc: true,
      content: {
        canvas,
        product: {
          creation_flow_steps: [],
          drawers: [],
          in_page_edits: [],
        } as ProductDocumentData,
        formats,
        extras,
      },
      variants,
      museSlug: solidusSlug,
      productSku: solidusSlug,
      // ^ TEMP: not sure how this will work for test/WIP products
      upsells: [],
      extras: [],
    };
  }

  const usesPdc =
    !productResponse.value.data.data.prismic ||
    productResponse.value.data.data.prismic.data.main__canvas2;

  // If this a PDC book but we have no Canvas Prismic docs, throw an error on prod
  if (
    env("NEXT_PUBLIC_CANVAS_ENV") === "production" &&
    usesPdc &&
    !productResponse.value.data.data.canvasPrismic
  ) {
    const error = Error(
      `No Canvas Prismic documents found for market "${marketplace}" and slug "${solidusSlug}"`
    );

    Bugsnag.notify(error, (event) => {
      event.severity = "error";
      event.unhandled = false;

      event.addMetadata("Metadata", {
        productResponse: productResponse.value.data.data,
        prismicRef,
      });
    });

    throw error;
  }

  const product =
    productResponse.value.data.data.canvasPrismic?.data ??
    ({
      creation_flow_steps: [],
      drawers: [],
      in_page_edits: [],
    } as ProductDocumentData);

  return {
    usesPdc,
    content: {
      canvas,
      product,
      formats,
      extras,
    },
    variants,
    museSlug:
      productResponse.value.data.data.solidus.sublimator_attributes.muse_slug,
    productSku:
      productResponse.value.data.data.solidus.sublimator_attributes.master_sku,
    upsells: productResponse.value.data.data.eagle,
  };
}

export function useGetProduct() {
  const router = useRouter();

  const marketplace = router.query.marketplace as string;
  const solidusSlug = router.query.solidus_slug as string;

  return useQuery(
    productKeys.product(solidusSlug, marketplace),
    async () => getProduct(solidusSlug, marketplace),
    {
      enabled: false,
      // ^ Ensures the query isn't refetched
    }
  );
}

/**
 * Fetch a URL to redirect to on completion
 */
export async function getRedirectUrl(
  ...[productSku, customisation]: Parameters<
    (typeof productKeys)["redirectUrl"]
  >
) {
  const response: AxiosResponse<RedirectUrlResponse> = await axios.post(
    "/website-api/v1/return-to-product/" + productSku,
    customisation
  );

  const url = new URL(response.data.url);

  // TODO Change website API to return a relative URL
  return url.pathname + url.search;
}

/**
 * Returns a URL to redirect the user to, for books that use Website formats/add-ons.
 */
export function useGetRedirectUrl() {
  const router = useRouter();

  const { data: customisation } = useGetCustomisation();
  const { data: product } = useGetProduct();

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }
  /**
   * This API relies on the original naming of `marketplace` as `prefix`.
   *
   * TEMP: As it's eventually going to be replaced by Formats + Add-ons pages in PDC,
   * we just manually set the `prefix` field for now.
   */
  const customisationWithPrefix = {
    ...customisation,
    product_slug: product.productSku,
    prefix: router.query.marketplace,
  };

  return useQuery<UseGetRedirectUrlResponse, AxiosError>(
    productKeys.redirectUrl(product.productSku, customisationWithPrefix),
    async () => getRedirectUrl(product.productSku, customisationWithPrefix),
    {
      enabled:
        !!customisation &&
        // ^ Don't enable this query before Canvas is ready
        !product.content.product.uses_pdc_formats_page,
      // ^ Or if we already know it's a PDC formats book
    }
  );
}

/**
 * Fetch customisation data from URL params, or local storage
 */
export function useGetCustomisation() {
  const router = useRouter();

  const { data: product } = useGetProduct();
  const { mutate: setCustomisation } = useSetCustomisation();

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }

  return useQuery<UseGetCustomisationResponse, Error>({
    queryKey: productKeys.customisation,
    queryFn: async () => {
      // If we have URL params, (re)initialise the localStorage entry with those.
      // (This allows us to reset a book to start or for editing)
      const urlParams = getNormalisedParsedQuery(
        router.asPath.replace(/#.*/, "")
      );

      if (!_isEmpty(urlParams)) {
        setCustomisation({
          data: { ...urlParams, product_slug: product.museSlug },
          behaviour: "replace",
        });

        const newUrl = getNormalisedUrl(router.asPath, {});
        router.replace(newUrl, undefined, {
          scroll: false,
          shallow: true,
        });

        return null;
      }

      const customisation = localStorage.getItem(product.museSlug);

      if (customisation) {
        try {
          // Check if the customisation is still valid JSON
          return JSON.parse(customisation) as ParsedQs;
        } catch (_) {
          // Clear the entry if it's no longer valid,
          // but don't re-throw the error as we'll handle it below.
          localStorage.removeItem(product.museSlug);

          const error = Error("Invalid customisation found");

          Bugsnag.notify(error, (event) => {
            event.addMetadata("Customisation", {
              invalidCustomisation: customisation,
            });
            event.severity = "warning";
            event.unhandled = false;
          });

          throw error;
        }
      }

      throw Error("No customisation found");
    },
    enabled: router.isReady,
  });
}

/**
 * Save customisation data to local storage
 */
export function useSetCustomisation(
  options?: UseMutationOptions<ParsedQs, Error, SetCustomisationArgs>
) {
  const queryClient = useQueryClient();

  const { data: product } = useGetProduct();

  const combineCustomisation = useGetCombinedCustomisation();

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }

  return useMutation<ParsedQs, Error, SetCustomisationArgs>({
    mutationFn: async ({ behaviour = "merge", data }) => {
      const customisation =
        queryClient.getQueryData<UseGetCustomisationResponse>(
          productKeys.customisation
        ) || {};

      // This handles merging the new data with
      const combinedCustomisation = combineCustomisation({
        oldData: behaviour === "merge" ? customisation : {},
        newData: data,
      });

      localStorage.setItem(
        product.museSlug,
        JSON.stringify(combinedCustomisation)
      );

      return combinedCustomisation;
    },
    ...(options ?? {}),
    onSettled: (data, error, variables, context) => {
      // Use the successful response to update the query cache so it doesn't need refetching
      queryClient.setQueryData(productKeys.customisation, data);

      options?.onSettled?.(data, error, variables, context);
    },
  });
}
